import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import lmsApi from '../services/api';
import LMSStaffLogin from '../pages/staff/LMSStaffLogin';
import LMSLogin from '../pages/customer/LMSLogin';

interface LMSProtectedRouteProps {
    children: React.ReactNode;
    requireStaff?: boolean;
}

const LMSProtectedRoute: React.FC<LMSProtectedRouteProps> = ({ children, requireStaff = false }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const checkSession = useCallback(async () => {
        setIsLoading(true);
        setIsAuthorized(false);
        setShowLogin(false);
        try {
            const response = await lmsApi.post('/auth/check-session/');
            const { authenticated, user_type } = response.data;

            if (authenticated) {
                if (requireStaff && user_type !== 'staff') {
                    // Authenticated as customer but needs staff — show staff login in-place
                    setShowLogin(true);
                } else if (!requireStaff && user_type === 'staff' && (location.pathname === '/lms/dashboard' || location.pathname === '/lms/dashboard/')) {
                    // Staff trying to access customer dashboard — redirect to staff dashboard
                    navigate('/lms/staff/dashboard');
                } else {
                    setIsAuthorized(true);
                }
            } else {
                // Not authenticated at all — show appropriate login in-place
                setShowLogin(true);
            }
        } catch (error) {
            console.error("Session check failed", error);
            setShowLogin(true);
        } finally {
            setIsLoading(false);
        }
    }, [location.pathname, requireStaff, navigate]);

    useEffect(() => {
        checkSession();
    }, [checkSession]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (showLogin) {
        // Pass onLoginSuccess so the login form can trigger a re-check without navigating
        return requireStaff
            ? <LMSStaffLogin onLoginSuccess={checkSession} />
            : <LMSLogin onLoginSuccess={checkSession} />;
    }

    return isAuthorized ? <>{children}</> : null;
};

export default LMSProtectedRoute;
